import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogOverlay } from '../ui/dialog';
import { eventInfo } from '../../data/landingData';

export const ExitIntent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  const handleMouseLeave = useCallback((e) => {
    // Only trigger if mouse leaves from top of viewport
    if (e.clientY < 10 && !hasShown) {
      setIsOpen(true);
      setHasShown(true);
      // Store in sessionStorage to prevent showing again
      sessionStorage.setItem('exitIntentShown', 'true');
    }
  }, [hasShown]);

  useEffect(() => {
    // Check if already shown this session
    if (sessionStorage.getItem('exitIntentShown')) {
      setHasShown(true);
      return;
    }

    // Add event listener after a delay (don't show immediately)
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogOverlay className="bg-black/80 backdrop-blur-sm" />
      <DialogContent 
        className="sm:max-w-[500px] bg-[#0A0A0A] border border-white/10 p-0 overflow-hidden"
        data-testid="exit-intent-popup"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors z-10"
          data-testid="exit-intent-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Background image */}
        <div 
          className="h-40 bg-cover bg-center relative"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1614595145313-c7e1765be457?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxibGFjayUyMG1lbiUyMG1lZGl0YXRpb24lMjBuYXR1cmV8ZW58MHx8fHwxNzc0NzAzOTIyfDA&ixlib=rb-4.1.0&q=85')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]" />
        </div>

        {/* Content */}
        <div className="p-8 pt-4 text-center">
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.2em] text-xs font-bold mb-3">
            Wait, King
          </p>
          
          <h3 className="font-cormorant text-3xl md:text-4xl font-medium text-white mb-4">
            Your Journey Starts Here
          </h3>
          
          <p className="text-zinc-400 font-manrope mb-6 leading-relaxed">
            Don't miss this opportunity to transform your life. Secure your spot with just{' '}
            <span className="text-[#D4AF37] font-semibold">${eventInfo.paymentPlanAmount} down</span>{' '}
            and join a brotherhood of men committed to growth.
          </p>

          {/* Urgency indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="relative">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse-urgency" />
            </div>
            <span className="text-sm text-zinc-300 font-manrope">
              Only <span className="text-red-400 font-semibold">{eventInfo.spotsRemaining} spots</span> remaining
            </span>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <a
              href="#pricing"
              onClick={handleClose}
              className="block w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-lg font-manrope font-semibold text-lg hover:bg-[#B8860B] transition-colors gold-glow"
              data-testid="exit-intent-cta"
            >
              Secure My Spot — ${eventInfo.paymentPlanAmount} Down
            </a>
            
            <button
              onClick={handleClose}
              className="w-full text-zinc-500 hover:text-zinc-300 font-manrope text-sm py-2 transition-colors"
              data-testid="exit-intent-dismiss"
            >
              I'll decide later
            </button>
          </div>

          {/* Trust elements */}
          <p className="text-xs text-zinc-600 mt-4 font-manrope">
            {eventInfo.dates} • {eventInfo.location}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
